// src/hooks/useAuth.js
import { useState, useEffect, createContext, useContext } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const googleLogin = async (googlePayload) => {
    try {
      console.log('Attempting Google login with payload:', googlePayload);
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3005'}/api/auth/google`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        credentials: 'include',
        mode: 'cors',
        body: JSON.stringify(googlePayload)
      });

      console.log('Google login response status:', response.status);
      
      if (response.ok) {
        const userData = await response.json();
        console.log('Google login successful, user data:', userData);
        setUser(userData);
        setIsAuthenticated(true);
        return { success: true };
      } else {
        const errorData = await response.json();
        console.error('Google login failed:', errorData);
        return { success: false, error: errorData.error };
      }
    } catch (error) {
      console.error('Google login network error:', error);
      return { success: false, error: 'Network error' };
    }
  };

  useEffect(() => {
    checkAuthStatus();
  }, []);

  // Update global session stats when user changes
  useEffect(() => {
    if (user) {
      window.sessionStats = {
        ...window.sessionStats,
        balance: parseFloat(user.balance) || 0,
        totalTokens: user.totalTokensUsed || 0,
        totalCost: parseFloat(user.totalCost) || 0
      };
    }
  }, [user]);

  const checkAuthStatus = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3005'}/api/auth/me`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        credentials: 'include',
        mode: 'cors'
      });
      
      if (response.ok) {
        const userData = await response.json();
        console.log('Auth check successful:', userData.email);
        setUser(userData);
        setIsAuthenticated(true);
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.log('Auth check failed:', response.status, errorData);
        setUser(null);
        setIsAuthenticated(false);
        
        // Clear any stored session data
        if (errorData.needsLogin) {
          console.log('Server indicates user needs to login again');
        }
      }
    } catch (error) {
      console.error('Auth check network error:', error);
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3005'}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        credentials: 'include',
        mode: 'cors',
        body: JSON.stringify({ email, password })
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        setIsAuthenticated(true);
        return { success: true };
      } else {
        const errorData = await response.json();
        return { success: false, error: errorData.error };
      }
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  };

  const register = async (email, password, username) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3005'}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        credentials: 'include',
        mode: 'cors',
        body: JSON.stringify({ email, password, username })
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        setIsAuthenticated(true);
        return { success: true };
      } else {
        const errorData = await response.json();
        return { success: false, error: errorData.error };
      }
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  };

  const logout = async () => {
    try {
      await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3005'}/api/auth/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        mode: 'cors'
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  const value = {
    user,
    isLoading,
    isAuthenticated,
    login,
    register,
    logout,
    checkAuthStatus,
    googleLogin
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};